import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle2, XCircle, MinusCircle, HelpCircle, Hourglass, Check, X, Eye, Brain, Wrench, Camera, GraduationCap, ChevronDown, ChevronUp
} from 'lucide-react';
import Button from '../ui/Button';
import type { RunAction, TraceStep, Verdict, WindowMetrics } from '../../services/trafficManagerService';

// ---------------------------------------------------------------------------
// Formatação no idioma de quem está vendo
// ---------------------------------------------------------------------------
const LOCALES: Record<string, string> = { pt: 'pt-BR', en: 'en-US', es: 'es-ES', fr: 'fr-FR', de: 'de-DE', it: 'it-IT', pl: 'pl-PL', da: 'da-DK', fil: 'fil-PH', zh: 'zh-CN' };
export const useFmt = () => {
  const { i18n } = useTranslation();
  const locale = LOCALES[(i18n.language || 'pt').split('-')[0]] || 'pt-BR';
  const money = (v: number | string | null | undefined, cur = 'BRL', compact = false) => {
    const n = typeof v === 'string' ? parseFloat(v) : (v ?? 0);
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency: cur || 'BRL', ...(compact ? { notation: 'compact', maximumFractionDigits: 1 } : {}) }).format(Number.isFinite(n) ? n : 0);
    } catch { return `${cur} ${n}`; }
  };
  const int = (n: number) => new Intl.NumberFormat(locale).format(Math.round(n || 0));
  const dec = (n: number, d = 1) => new Intl.NumberFormat(locale, { maximumFractionDigits: d }).format(n || 0);
  const dateTime = (iso?: string | null) => (iso ? new Date(iso).toLocaleString(locale, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');
  const day = (iso: string) => {
    const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString(locale, { day: '2-digit', month: '2-digit' });
  };
  const relative = (iso?: string | null) => {
    if (!iso) return '—';
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    if (Math.abs(diff) < 3600) return rtf.format(-Math.round(diff / 60), 'minute');
    if (Math.abs(diff) < 86400) return rtf.format(-Math.round(diff / 3600), 'hour');
    return rtf.format(-Math.round(diff / 86400), 'day');
  };
  return { money, int, dec, dateTime, day, relative, locale };
};

// ---------------------------------------------------------------------------
// Rótulos
// ---------------------------------------------------------------------------
export const useLabels = () => {
  const { t } = useTranslation();
  const platform = (p: string) => ({ google: 'Google', meta: 'Meta', linkedin: 'LinkedIn', openai: 'ChatGPT' } as Record<string, string>)[p] || p;
  const goal = (g: string) => ({
    conversions: t('trafficManager.goals.conversions', 'Conversões'),
    leads: t('trafficManager.goals.leads', 'Leads'),
    sales: t('trafficManager.goals.sales', 'Vendas (ROAS)'),
    traffic: t('trafficManager.goals.traffic', 'Tráfego (cliques)'),
    awareness: t('trafficManager.goals.awareness', 'Alcance')
  } as Record<string, string>)[g] || g;
  const action = (tool: string) => ({
    set_daily_budget: t('trafficManager.actions.set_daily_budget', 'Orçamento diário'),
    pause_campaign: t('trafficManager.actions.pause_campaign', 'Pausar campanha'),
    resume_campaign: t('trafficManager.actions.resume_campaign', 'Reativar campanha'),
    pause_ad: t('trafficManager.actions.pause_ad', 'Pausar anúncio'),
    resume_ad: t('trafficManager.actions.resume_ad', 'Reativar anúncio'),
    add_keywords: t('trafficManager.actions.add_keywords', 'Palavras-chave positivas'),
    add_negative_keywords: t('trafficManager.actions.add_negative_keywords', 'Palavras negativas'),
    pause_keyword: t('trafficManager.actions.pause_keyword', 'Pausar palavra-chave'),
    enable_keyword: t('trafficManager.actions.enable_keyword', 'Reativar palavra-chave'),
    create_automation_rule: t('trafficManager.actions.create_automation_rule', 'Nova automação'),
    set_automation_rule_status: t('trafficManager.actions.set_automation_rule_status', 'Ligar/desligar automação'),
    create_website_audience: t('trafficManager.actions.create_website_audience', 'Nova audiência de visitantes'),
    create_remarketing_campaign: t('trafficManager.actions.create_remarketing_campaign', 'Nova campanha de remarketing'),
    set_google_ai_max: t('trafficManager.actions.set_google_ai_max', 'AI Max do Google'),
    update_targeting: t('trafficManager.actions.update_targeting', 'Ajustar público e localização'),
    create_ad: t('trafficManager.actions.create_ad', 'Criar anúncio novo')
  } as Record<string, string>)[tool] || tool;
  const readTool = (tool: string) => ({
    get_daily_breakdown: t('trafficManager.reads.get_daily_breakdown', 'Leu o dia a dia'),
    get_campaign_metrics: t('trafficManager.reads.get_campaign_metrics', 'Leu as métricas'),
    get_campaign_keywords: t('trafficManager.reads.get_campaign_keywords', 'Analisou as palavras-chave'),
    get_search_terms: t('trafficManager.reads.get_search_terms', 'Analisou os termos de busca'),
    list_ads: t('trafficManager.reads.list_ads', 'Listou os anúncios'),
    get_ad_metrics: t('trafficManager.reads.get_ad_metrics', 'Leu as métricas do anúncio'),
    get_campaign_platform_status: t('trafficManager.reads.get_campaign_platform_status', 'Conferiu o status na plataforma'),
    get_google_settings: t('trafficManager.reads.get_google_settings', 'Conferiu os lances no Google'),
    get_google_recommendations: t('trafficManager.reads.get_google_recommendations', 'Leu as recomendações do Google'),
    get_change_history: t('trafficManager.reads.get_change_history', 'Consultou o próprio histórico'),
    list_audiences: t('trafficManager.reads.list_audiences', 'Conferiu as audiências de remarketing'),
    get_ads_health: t('trafficManager.reads.get_ads_health', 'Conferiu a saúde das contas'),
    get_targeting: t('trafficManager.reads.get_targeting', 'Leu a segmentação na plataforma'),
    get_segment_performance: t('trafficManager.reads.get_segment_performance', 'Analisou o desempenho por região, idade, gênero e dispositivo'),
    search_locations: t('trafficManager.reads.search_locations', 'Buscou localizações'),
    get_materials: t('trafficManager.reads.get_materials', 'Consultou os materiais')
  } as Record<string, string>)[tool] || action(tool);
  const status = (s: string) => ({
    executed: t('trafficManager.status.executed', 'Executada'),
    approved: t('trafficManager.status.approved', 'Aprovada'),
    proposed: t('trafficManager.status.proposed', 'Aguardando você'),
    rejected: t('trafficManager.status.rejected', 'Recusada'),
    blocked: t('trafficManager.status.blocked', 'Barrada pela trava'),
    failed: t('trafficManager.status.failed', 'Falhou'),
    expired: t('trafficManager.status.expired', 'Expirou')
  } as Record<string, string>)[s] || s;
  return { platform, goal, action, readTool, status };
};

export const STATUS_CLS: Record<string, string> = {
  executed: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  approved: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  proposed: 'bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-300',
  rejected: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  expired: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
  blocked: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
};

export const StatusPill: React.FC<{ status: string }> = ({ status }) => {
  const { status: lbl } = useLabels();
  return <span className={`px-2 py-0.5 text-[11px] font-medium rounded-full whitespace-nowrap ${STATUS_CLS[status] || STATUS_CLS.executed}`}>{lbl(status)}</span>;
};

// ---------------------------------------------------------------------------
// Veredito do efeito (status com ícone + texto, nunca só cor)
// ---------------------------------------------------------------------------
export const VerdictBadge: React.FC<{ v: Verdict; daysLeft?: number; reason?: string | null }> = ({ v, daysLeft, reason }) => {
  const { t } = useTranslation();
  const map: Record<Verdict, { icon: React.ElementType; label: string; cls: string }> = {
    melhorou: { icon: CheckCircle2, label: t('trafficManager.verdict.melhorou', 'Melhorou'), cls: 'text-emerald-700 bg-emerald-50 dark:text-emerald-300 dark:bg-emerald-900/30' },
    piorou: { icon: XCircle, label: t('trafficManager.verdict.piorou', 'Piorou'), cls: 'text-red-700 bg-red-50 dark:text-red-300 dark:bg-red-900/30' },
    neutro: { icon: MinusCircle, label: t('trafficManager.verdict.neutro', 'Sem efeito claro'), cls: 'text-gray-700 bg-gray-100 dark:text-gray-300 dark:bg-gray-700' },
    inconclusivo: { icon: HelpCircle, label: t('trafficManager.verdict.inconclusivo', 'Inconclusivo'), cls: 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-700' },
    maturando: { icon: Hourglass, label: t('trafficManager.verdict.maturando', 'Maturando'), cls: 'text-sky-700 bg-sky-50 dark:text-sky-300 dark:bg-sky-900/30' }
  };
  const x = map[v] || map.inconclusivo;
  const Icon = x.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${x.cls}`} title={reason || undefined}>
      <Icon className="w-3.5 h-3.5" />
      {x.label}
      {v === 'maturando' && daysLeft != null && daysLeft > 0 && <span className="opacity-80">· {t('trafficManager.verdict.daysLeft', '{{n}}d', { n: daysLeft })}</span>}
    </span>
  );
};

/** Antes x depois de uma mudança (as métricas que decidiram o veredito). */
export const BeforeAfter: React.FC<{ antes: WindowMetrics; depois: WindowMetrics; cur: string }> = ({ antes, depois, cur }) => {
  const { t } = useTranslation();
  const f = useFmt();
  const linhas: [string, string, string][] = [
    [t('trafficManager.metrics.cpa', 'Custo/conv.'), antes.cpa == null ? '—' : f.money(antes.cpa, cur), depois.cpa == null ? '—' : f.money(depois.cpa, cur)],
    [t('trafficManager.metrics.convPerDay', 'Conv./dia'), f.dec(antes.conv_dia, 2), f.dec(depois.conv_dia, 2)],
    [t('trafficManager.metrics.cpc', 'CPC'), antes.cpc == null ? '—' : f.money(antes.cpc, cur), depois.cpc == null ? '—' : f.money(depois.cpc, cur)],
    [t('trafficManager.metrics.ctr', 'CTR'), `${f.dec(antes.ctr, 2)}%`, `${f.dec(depois.ctr, 2)}%`],
    [t('trafficManager.metrics.spendPerDay', 'Gasto/dia'), f.money(antes.gasto_dia, cur), f.money(depois.gasto_dia, cur)]
  ];
  return (
    <table className="text-xs w-full max-w-sm">
      <thead>
        <tr className="text-gray-500"><th className="text-left font-normal py-0.5" /><th className="text-right font-normal">{t('trafficManager.metrics.before', 'Antes')}</th><th className="text-right font-normal">{t('trafficManager.metrics.after', 'Depois')}</th></tr>
      </thead>
      <tbody className="text-gray-800 dark:text-gray-200">
        {linhas.map(([l, a, d]) => (
          <tr key={l}><td className="py-0.5 text-gray-500">{l}</td><td className="text-right tabular-nums">{a}</td><td className="text-right tabular-nums font-medium">{d}</td></tr>
        ))}
      </tbody>
    </table>
  );
};

// ---------------------------------------------------------------------------
// Uma ação do robô
// ---------------------------------------------------------------------------
// O plano vem da IA: lista pode chegar como texto, lista de textos ou de objetos.
const comoLista = (v: any): any[] => (Array.isArray(v) ? v : v == null || v === '' ? [] : typeof v === 'string' ? v.split(/[,\n]/).map((x) => x.trim()).filter(Boolean) : [v]);
const textoDe = (k: any): string => (k && typeof k === 'object' ? String(k.text ?? k.keyword ?? k.name ?? '') : String(k ?? ''));

export const describeAction = (a: RunAction, money: (v: number, c?: string) => string, cur: string): string => {
  if (a.tool === 'set_daily_budget' && a.plan && a.plan.para != null) return `${money(a.plan.de ?? 0, cur)} → ${money(a.plan.para, cur)}`;
  if (a.tool === 'add_negative_keywords') return comoLista(a.plan?.keywords ?? a.input?.keywords).map(textoDe).filter(Boolean).slice(0, 10).join(', ');
  if (a.tool === 'add_keywords') return comoLista(a.plan?.keywords ?? a.input?.keywords).map((k) => (k && typeof k === 'object' && k.match_type ? `${textoDe(k)} [${k.match_type}]` : textoDe(k))).filter(Boolean).slice(0, 8).join(', ');
  if (a.tool === 'pause_keyword' || a.tool === 'enable_keyword') return String((a.plan as any)?.keyword || '');
  if (a.tool === 'create_automation_rule') return `${(a.plan as any)?.rule_name || ''}`;
  if (a.tool === 'set_automation_rule_status') return `${(a.plan as any)?.rule_name || ''} → ${a.input?.status}`;
  if (a.tool === 'create_remarketing_campaign') { const p = a.plan as any; return p ? `${p.audience_name} · ${p.platform === 'meta' ? 'Meta' : 'Google'} · ${money(p.daily_budget, cur)}/dia` : ''; }
  if (a.tool === 'update_targeting') { const p = a.plan as any; return p ? String(p.resumo || '') : ''; }
  if (a.tool === 'create_ad') { const p = a.plan as any; return p ? (p.plataforma === 'meta' ? `Meta · ${p.material || ''} · "${p.headline || ''}"` : `Google · ${p.grupo || ''} · "${textoDe(comoLista(p.headlines)[0])}"`) : ''; }
  if (a.tool === 'set_google_ai_max') return (a.input?.enable ? '→ ligar' : '→ desligar');
  if (a.tool === 'create_website_audience') { const p = a.plan as any; return p ? `${comoLista(p.platforms).map((x: string) => (x === 'meta' ? 'Meta' : 'Google')).join(' + ')} · ${p.retention_days}d${p.url_contains ? ` · "${p.url_contains}"` : ''}` : ''; }
  return '';
};

export const ActionRow: React.FC<{
  a: RunAction;
  cur: string;
  busy?: boolean;
  onApprove?: () => void;
  onReject?: () => void;
  extra?: React.ReactNode;
}> = ({ a, cur, busy, onApprove, onReject, extra }) => {
  const { t } = useTranslation();
  const f = useFmt();
  const L = useLabels();
  const detalhe = describeAction(a, f.money, cur);
  return (
    <div className="py-3 flex flex-col sm:flex-row sm:items-start gap-2">
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-sm font-medium text-gray-900 dark:text-white">{L.action(a.tool)}</span>
          {a.campaign_name && <span className="text-sm text-gray-600 dark:text-gray-300 truncate max-w-[18rem]">· {a.campaign_name}</span>}
          <StatusPill status={a.status} />
        </div>
        {detalhe && <div className="text-sm text-gray-800 dark:text-gray-200 mt-0.5 break-words">{detalhe}</div>}
        {a.reason && <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">{a.reason}</div>}
        {a.verification && ['executed', 'approved'].includes(a.status) && (
          <div className={`mt-1 inline-flex items-center gap-1 text-[11px] font-medium ${a.verification.status === 'ok' ? 'text-emerald-700 dark:text-emerald-400' : a.verification.status === 'mismatch' ? 'text-red-700 dark:text-red-400' : 'text-gray-500'}`}>
            {a.verification.status === 'ok' ? <CheckCircle2 className="w-3.5 h-3.5" /> : a.verification.status === 'mismatch' ? <XCircle className="w-3.5 h-3.5" /> : <HelpCircle className="w-3.5 h-3.5" />}
            {a.verification.status === 'ok' ? t('trafficManager.verify.ok', 'Conferido na plataforma')
              : a.verification.status === 'mismatch' ? t('trafficManager.verify.mismatch', 'A plataforma não confirmou: mostra {{o}}', { o: String(a.verification.observed) })
              : a.verification.status === 'pending' ? t('trafficManager.verify.pending', 'Publicando; o resultado chega por notificação')
              : t('trafficManager.verify.unverifiable', 'Sem conferência ao vivo nesta plataforma')}
          </div>
        )}
        {a.error && <div className="text-xs text-orange-700 dark:text-orange-300 mt-1">{a.error}</div>}
        {extra}
      </div>
      {a.status === 'proposed' && onApprove && onReject && (
        <div className="flex gap-2 shrink-0">
          <Button size="sm" variant="secondary" onClick={onReject} disabled={busy} leftIcon={<X className="w-3.5 h-3.5" />}>{t('trafficManager.reject', 'Recusar')}</Button>
          <Button size="sm" onClick={onApprove} disabled={busy} leftIcon={<Check className="w-3.5 h-3.5" />}>{t('trafficManager.approve', 'Aprovar')}</Button>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Rastreio: o passo a passo do raciocínio de uma rodada
// ---------------------------------------------------------------------------
export const TraceView: React.FC<{ steps: TraceStep[] }> = ({ steps }) => {
  const { t } = useTranslation();
  const L = useLabels();
  const [aberto, setAberto] = useState<Record<number, boolean>>({});
  if (!steps || !steps.length) return <p className="text-xs text-gray-500">{t('trafficManager.trace.empty', 'Sem rastreio para esta rodada.')}</p>;
  const icone = (s: TraceStep) => {
    if (s.tipo === 'retrato') return <Camera className="w-3.5 h-3.5" />;
    if (s.tipo === 'pensamento') return <Brain className="w-3.5 h-3.5" />;
    if (s.tipo === 'acao') return <Wrench className="w-3.5 h-3.5" />;
    if (s.tipo === 'aprendizado') return <GraduationCap className="w-3.5 h-3.5" />;
    return <Eye className="w-3.5 h-3.5" />;
  };
  return (
    <ol className="relative border-l border-gray-200 dark:border-gray-700 ml-2 space-y-3">
      {steps.map((s) => {
        const titulo = s.tipo === 'retrato' ? t('trafficManager.trace.snapshot', 'Montou o retrato da conta')
          : s.tipo === 'pensamento' ? t('trafficManager.trace.thought', 'Raciocínio')
          : s.tipo === 'aprendizado' ? t('trafficManager.trace.learned', 'Registrou lições')
          : s.tipo === 'acao' ? L.action(s.ferramenta || '') : L.readTool(s.ferramenta || '');
        const temDetalhe = !!(s.entrada || s.resultado);
        return (
          <li key={s.n} className="ml-4">
            <span className={`absolute -left-[9px] mt-0.5 flex items-center justify-center w-[18px] h-[18px] rounded-full ring-2 ring-white dark:ring-gray-900 ${s.tipo === 'acao' ? (s.ok ? 'bg-primary-600 text-white' : 'bg-orange-500 text-white') : s.tipo === 'pensamento' ? 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
              {icone(s)}
            </span>
            <div className="text-xs font-medium text-gray-900 dark:text-white flex items-center gap-2">
              {titulo}
              {s.tipo === 'acao' && s.ok === false && <span className="text-orange-600 dark:text-orange-400 font-normal">{t('trafficManager.trace.refused', 'recusada')}</span>}
              {temDetalhe && (
                <button type="button" onClick={() => setAberto((p) => ({ ...p, [s.n]: !p[s.n] }))} className="text-gray-400 hover:text-gray-600" aria-label={t('trafficManager.trace.details', 'Detalhes') as string}>
                  {aberto[s.n] ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
              )}
            </div>
            {s.texto && <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5 whitespace-pre-line">{s.texto}</p>}
            {aberto[s.n] && (
              <div className="mt-1 space-y-1">
                {s.entrada && <pre className="text-[11px] bg-gray-50 dark:bg-gray-800 rounded p-2 whitespace-pre-wrap break-all text-gray-600 dark:text-gray-300">{s.entrada}</pre>}
                {s.resultado && <pre className="text-[11px] bg-gray-50 dark:bg-gray-800 rounded p-2 whitespace-pre-wrap break-all text-gray-600 dark:text-gray-300 max-h-48 overflow-y-auto">{s.resultado}</pre>}
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
};

/** Tema dos gráficos: acompanha a troca de claro/escuro do app. */
export const useIsDark = () => {
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));
  useEffect(() => {
    const obs = new MutationObserver(() => setDark(document.documentElement.classList.contains('dark')));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  return dark;
};

/** Cores de dados validadas (claro/escuro): série 1 azul, série 2 laranja, ideal neutro. */
export const useChartColors = () => {
  const dark = useIsDark();
  return dark
    ? { s1: '#3987e5', s2: '#d95926', ideal: '#8a8984', grid: '#2e2e2c', axis: '#a3a29c', marker: '#9085e9', surface: '#1a1a19', text: '#f0efec' }
    : { s1: '#2a78d6', s2: '#eb6834', ideal: '#8f8e8a', grid: '#ecebe8', axis: '#6f6e69', marker: '#6250d6', surface: '#ffffff', text: '#0b0b0b' };
};

export const SectionTitle: React.FC<{ icon?: React.ElementType; children: React.ReactNode; right?: React.ReactNode }> = ({ icon: Icon, children, right }) => (
  <div className="flex items-center gap-2 mb-4">
    {Icon && <Icon className="w-5 h-5 text-primary-600" />}
    <h2 className="font-semibold text-gray-900 dark:text-white">{children}</h2>
    {right && <div className="ml-auto">{right}</div>}
  </div>
);

// ---------------------------------------------------------------------------
// Texto do robô: markdown leve (títulos, negrito, listas), renderizado como
// elementos React — nada de innerHTML com texto vindo da IA.
// ---------------------------------------------------------------------------
const inline = (txt: string, keyBase: string): React.ReactNode[] => {
  const out: React.ReactNode[] = [];
  const re = /\*\*([^*]+)\*\*/g;
  let last = 0; let m: RegExpExecArray | null; let i = 0;
  while ((m = re.exec(txt))) {
    if (m.index > last) out.push(txt.slice(last, m.index));
    out.push(<strong key={`${keyBase}-b${i++}`} className="font-semibold text-gray-900 dark:text-white">{m[1]}</strong>);
    last = m.index + m[0].length;
  }
  if (last < txt.length) out.push(txt.slice(last));
  return out;
};

export const RichText: React.FC<{ text?: string | null; className?: string; clamp?: boolean }> = ({ text, className = '', clamp }) => {
  if (!text) return null;
  const linhas = text.replace(/\r/g, '').split('\n');
  const blocos: React.ReactNode[] = [];
  let lista: { ordered: boolean; itens: string[] } | null = null;
  const fecharLista = (k: number) => {
    if (!lista) return;
    const L = lista; lista = null;
    blocos.push(L.ordered
      ? <ol key={`l${k}`} className="list-decimal pl-5 space-y-0.5">{L.itens.map((it, j) => <li key={j}>{inline(it, `l${k}-${j}`)}</li>)}</ol>
      : <ul key={`l${k}`} className="list-disc pl-5 space-y-0.5">{L.itens.map((it, j) => <li key={j}>{inline(it, `l${k}-${j}`)}</li>)}</ul>);
  };
  linhas.forEach((raw, k) => {
    const l = raw.trimEnd();
    const bullet = /^\s*[-*•]\s+(.*)$/.exec(l);
    const num = /^\s*\d+[.)]\s+(.*)$/.exec(l);
    if (bullet || num) {
      const ordered = !!num;
      if (lista && lista.ordered !== ordered) fecharLista(k);
      if (!lista) lista = { ordered, itens: [] };
      lista.itens.push((bullet || num)![1]);
      return;
    }
    fecharLista(k);
    if (!l.trim()) return;
    const h = /^#{1,4}\s+(.*)$/.exec(l);
    if (h) { blocos.push(<p key={k} className="font-semibold text-gray-900 dark:text-white mt-1">{inline(h[1].replace(/\*\*/g, ''), `h${k}`)}</p>); return; }
    blocos.push(<p key={k}>{inline(l, `p${k}`)}</p>);
  });
  fecharLista(linhas.length);
  return <div className={`space-y-1.5 ${clamp ? 'max-h-80 overflow-hidden [mask-image:linear-gradient(to_bottom,black_80%,transparent)]' : ''} ${className}`}>{blocos}</div>;
};

/** Gatilhos e ações das automações em linguagem de gente. */
export const useRuleText = () => {
  const { t } = useTranslation();
  const f = useFmt();
  return (gatilho: string, valor: number, acao: string, cur: string) => {
    const money = ['cpc_above', 'cpc_below', 'cpa_above', 'cpa_below', 'spend_above', 'spend_below'].includes(gatilho);
    const v = money ? f.money(valor, cur) : gatilho.startsWith('ctr') ? `${f.dec(valor, 2)}%` : gatilho.startsWith('roas') ? `${f.dec(valor, 2)}x` : f.int(valor);
    const g = ({
      ctr_below: t('trafficManager.rules.ctr_below', 'CTR abaixo de {{v}}', { v }), ctr_above: t('trafficManager.rules.ctr_above', 'CTR acima de {{v}}', { v }),
      cpc_above: t('trafficManager.rules.cpc_above', 'CPC acima de {{v}}', { v }), cpc_below: t('trafficManager.rules.cpc_below', 'CPC abaixo de {{v}}', { v }),
      cpa_above: t('trafficManager.rules.cpa_above', 'Custo/conv. acima de {{v}}', { v }), cpa_below: t('trafficManager.rules.cpa_below', 'Custo/conv. abaixo de {{v}}', { v }),
      spend_above: t('trafficManager.rules.spend_above', 'Gasto acima de {{v}}', { v }), spend_below: t('trafficManager.rules.spend_below', 'Gasto abaixo de {{v}}', { v }),
      roas_above: t('trafficManager.rules.roas_above', 'ROAS acima de {{v}}', { v }), roas_below: t('trafficManager.rules.roas_below', 'ROAS abaixo de {{v}}', { v }),
      impressions_below: t('trafficManager.rules.impressions_below', 'Impressões abaixo de {{v}}', { v }), clicks_below: t('trafficManager.rules.clicks_below', 'Cliques abaixo de {{v}}', { v })
    } as Record<string, string>)[gatilho] || `${gatilho} ${v}`;
    const a = ({
      pause_campaign: t('trafficManager.rules.a.pause_campaign', 'pausa a campanha'), enable_campaign: t('trafficManager.rules.a.enable_campaign', 'reativa a campanha'),
      adjust_budget: t('trafficManager.rules.a.adjust_budget', 'ajusta o orçamento'), adjust_bid: t('trafficManager.rules.a.adjust_bid', 'ajusta o lance'),
      send_notification: t('trafficManager.rules.a.send_notification', 'avisa você'), pause_ad: t('trafficManager.rules.a.pause_ad', 'pausa o anúncio'), enable_ad: t('trafficManager.rules.a.enable_ad', 'reativa o anúncio')
    } as Record<string, string>)[acao] || acao;
    return `${g} → ${a}`;
  };
};
